import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link as MuiLink,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useWhoAmI } from '../../auth/AdminGate';
import {
  AdminCategory,
  AdminItem,
  ApiError,
  ItemTenant,
  createCategory,
  listCategories,
  listItemTenants,
  listItemsByCategory,
  patchItem,
  SquareSyncReport,
  SquareSyncStatus,
  clearSquareCache,
  getSquareSyncStatus,
  syncSquare,
} from '../../api';
import ItemImage from '../widgets/ItemImage';
import ItemSearchBar from '../widgets/items/ItemSearchBar';
import Price from '../widgets/Price';
import BulkFlagDialog from '../dialogs/BulkFlagDialog';
import ItemEditDialog from '../dialogs/ItemEditDialog';

// In tqos-{admin,react}, root categories are children of a sentinel
// category with id "1". Mirroring that here keeps the tree-building
// logic equivalent.
const ROOT_PARENT_ID = '1';

// localStorage keys for the always-visible view toggles. Scoped to
// the items page so other features can pick their own keys without
// stomping. Versioned ("v1") so a future shape change can be rolled
// out without reading garbage from older sessions.
const LS_KEY_SHOW_DELETED = 'web-admin.items.show-deleted.v1';
const LS_KEY_EDIT_FLAGS = 'web-admin.items.edit-flags.v1';

// Custom event name used by `useLocalStorageBool` to notify peer hook
// instances when a write happens in this tab. The built-in `storage`
// event only fires in OTHER tabs, so without this two hooks in the
// same window reading the same key stay out of sync after a setter
// call. Detail carries the canonical 'true' / 'false' string.
const LS_BOOL_EVENT = 'web-admin:items:ls-bool';

/** Boolean state backed by localStorage. Falls back to `initial` when
 *  storage is absent (SSR / disabled) or holds anything other than the
 *  canonical 'true' / 'false' strings. Writes happen via the returned
 *  setter so consumers don't have to thread a separate persist step.
 *
 *  Multiple instances of this hook for the same key (e.g. one in the
 *  toggle bar and one in `CategoryPanel`) stay in sync via a custom
 *  in-window event plus the standard cross-tab `storage` event. */
const useLocalStorageBool = (
  key: string,
  initial: boolean,
): [boolean, (next: boolean) => void] => {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch {
      // Private mode / disabled storage — silently fall back to initial.
    }
    return initial;
  });

  // Subscribe to peer updates. Two channels: the native `storage`
  // event (other tabs) and our custom event (same tab). Both feed the
  // same setter so the hook converges to whatever was just written.
  useEffect(() => {
    const apply = (raw: string | null) => {
      if (raw === 'true') setValue(true);
      else if (raw === 'false') setValue(false);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      apply(e.newValue);
    };
    const onCustom = (e: Event) => {
      const ce = e as CustomEvent<{ key: string; value: string | null }>;
      if (ce.detail?.key !== key) return;
      apply(ce.detail.value);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(LS_BOOL_EVENT, onCustom as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(LS_BOOL_EVENT, onCustom as EventListener);
    };
  }, [key]);

  const set = (next: boolean) => {
    const raw = next ? 'true' : 'false';
    setValue(next);
    try {
      window.localStorage.setItem(key, raw);
    } catch {
      // Same posture as the read path: storage write failure is non-fatal.
    }
    // Notify peer hooks in this tab. CustomEvent doesn't bubble through
    // `storage`, which is window-cross-tab only.
    window.dispatchEvent(
      new CustomEvent(LS_BOOL_EVENT, { detail: { key, value: raw } }),
    );
  };
  return [value, set];
};

/** Always-visible "Show deleted" / "Edit flags" toggles. Rendered in
 *  the tenant picker (where they have no effect, but keep the items
 *  toolbar in a consistent place) as well as inside `CategoryPanel`
 *  (where they drive the grid). State lives in localStorage via
 *  `useLocalStorageBool` so each panel reads the same persisted value
 *  — the bar is "controlled" by storage, not by props. */
const ViewTogglesBar: React.FC = () => {
  const [showDeleted, setShowDeleted] = useLocalStorageBool(
    LS_KEY_SHOW_DELETED,
    false,
  );
  const [quickEditMode, setQuickEditMode] = useLocalStorageBool(
    LS_KEY_EDIT_FLAGS,
    false,
  );
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            size="small"
          />
        }
        label="Show deleted"
        sx={{ m: 0, color: '#475569' }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={quickEditMode}
            onChange={(e) => setQuickEditMode(e.target.checked)}
            size="small"
          />
        }
        label="Edit flags"
        sx={{ m: 0, color: '#475569' }}
      />
    </Box>
  );
};

const stockColor = (stock: number) => {
  if (stock > 0) return undefined;
  return '#b91c1c'; // red — out of stock
};

// A parent of variants — i.e. group.enabled is true and there's no
// groupId pointing the other way. We don't open the edit dialog for
// these; variant management is a separate flow (TBD).
const isParentItem = (item: AdminItem): boolean => {
  const g = item.group;
  if (!g?.enabled) return false;
  const gid = g.group_id ?? '';
  return gid.trim() === '';
};

// ---------------------------------------------------------------------------
// Tenant picker
// ---------------------------------------------------------------------------

const TenantsPanel: React.FC = () => {
  const { idToken } = useAuth();
  const [data, setData] = useState<ItemTenant[] | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    listItemTenants(idToken)
      .then((rows) => {
        if (!cancelled) setData(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load tenants');
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CenteredSpinner />;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
          Items
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ClearSquareCacheButton />
          <SquareSyncButton />
        </Box>
      </Box>
      <Box sx={{ mb: 3 }}>
        <ItemSearchBar />
      </Box>
      {/* Same toggles the per-tenant view shows, rendered here so the
          items-page toolbar reads the same at every level of the
          hierarchy. They don't drive anything on this view (no items
          are loaded yet) — the consistency is the point. */}
      <Box sx={{ mb: 3 }}>
        <ViewTogglesBar />
      </Box>
      <Grid container spacing={3}>
        {data.map((t) => (
          <Grid key={t.slug} item xs={12} sm={6} md={4}>
            <Card
              variant="outlined"
              sx={{
                opacity: t.enabled ? 1 : 0.5,
                pointerEvents: t.enabled ? 'auto' : 'none',
              }}
            >
              <CardActionArea
                component={Link}
                to={`/items/${t.slug}`}
                disabled={!t.enabled}
                sx={{ p: 4, minHeight: 200, textAlign: 'center' }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 500, color: '#1f2937' }}
                >
                  {t.label}
                </Typography>
                {!t.enabled && (
                  <Chip
                    label="Coming soon"
                    size="small"
                    sx={{ mt: 1.5, backgroundColor: '#e5e7eb' }}
                  />
                )}
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

// ---------------------------------------------------------------------------
// Square sync: preview-then-apply dialog
// ---------------------------------------------------------------------------

type SyncPhase = 'idle' | 'previewing' | 'applying' | 'done';

const SquareSyncButton: React.FC = () => {
  const { idToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<SyncPhase>('idle');
  const [report, setReport] = useState<SquareSyncReport | null>(null);
  const [error, setError] = useState<string>('');

  const reset = () => {
    setPhase('idle');
    setReport(null);
    setError('');
  };

  const runSync = async (apply: boolean) => {
    if (!idToken) return;
    setPhase(apply ? 'applying' : 'previewing');
    setError('');
    const outcome = await syncSquare(idToken, apply);
    if (outcome.kind === 'ok') {
      setReport(outcome.report);
      setPhase('done');
    } else if (outcome.kind === 'busy') {
      setError('Another sync is already running. Try again in a moment.');
      setPhase('idle');
    } else if (outcome.kind === 'not_configured') {
      setError('Square sync is not configured on this server.');
      setPhase('idle');
    } else {
      setError(`Sync failed (HTTP ${outcome.status}).`);
      setPhase('idle');
    }
  };

  const onOpen = () => {
    reset();
    setOpen(true);
    runSync(false);
  };

  const onClose = () => {
    setOpen(false);
    reset();
  };

  const totals = report
    ? report.per_db.reduce(
        (acc, d) => ({
          adds: acc.adds + d.adds,
          readds: acc.readds + d.readds,
          updates: acc.updates + d.updates,
        }),
        { adds: 0, readds: 0, updates: 0 },
      )
    : null;

  const nothingToDo =
    totals !== null && totals.adds + totals.readds + totals.updates === 0;

  return (
    <>
      <Button variant="outlined" onClick={onOpen} sx={{ textTransform: 'none' }}>
        Sync to Square
      </Button>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {report?.applied ? 'Sync applied' : 'Square sync preview'}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {(phase === 'previewing' || phase === 'applying') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                {phase === 'previewing'
                  ? 'Computing changes…'
                  : 'Applying changes to Square…'}
              </Typography>
            </Box>
          )}

          {phase === 'done' && report && (
            <>
              {nothingToDo ? (
                <Alert severity="success">
                  Everything is in sync — nothing to do.
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Tenant</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          Add
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          Re-add
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          Update
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.per_db.map((d) => (
                        <TableRow key={d.db_name}>
                          <TableCell>{d.db_name}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{d.adds}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{d.readds}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{d.updates}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          {totals!.adds}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          {totals!.readds}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          {totals!.updates}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 2, color: '#94a3b8' }}
              >
                {report.elapsed_ms} ms
                {report.used_cached_catalog ? ' · used cached Square catalog' : ' · fetched Square catalog'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          {phase === 'done' && !report?.applied && !nothingToDo && (
            <Button
              variant="contained"
              onClick={() => runSync(true)}
              sx={{ textTransform: 'none' }}
            >
              Apply
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Clear Square cache: small "in case of emergency" control next to the
// sync button. Forces the next sync to refetch the catalog from Square.
// ---------------------------------------------------------------------------

type ClearPhase = 'idle' | 'clearing' | 'done' | 'error';

const ClearSquareCacheButton: React.FC = () => {
  const { idToken } = useAuth();
  const [phase, setPhase] = useState<ClearPhase>('idle');
  const [message, setMessage] = useState<string>('');

  const onClick = async () => {
    if (!idToken || phase === 'clearing') return;
    setPhase('clearing');
    setMessage('');
    const outcome = await clearSquareCache(idToken);
    if (outcome.kind === 'ok') {
      setMessage(outcome.cleared ? 'Square cache cleared.' : 'Cache was already empty.');
      setPhase('done');
    } else if (outcome.kind === 'not_configured') {
      setMessage('Square sync is not configured on this server.');
      setPhase('error');
    } else {
      setMessage(`Clear failed (HTTP ${outcome.status}).`);
      setPhase('error');
    }
  };

  return (
    <>
      <Button
        variant="text"
        size="small"
        onClick={onClick}
        disabled={phase === 'clearing'}
        sx={{ textTransform: 'none', color: '#64748b' }}
      >
        {phase === 'clearing' ? 'Clearing…' : 'Clear cache'}
      </Button>
      <Snackbar
        open={phase === 'done' || phase === 'error'}
        autoHideDuration={4000}
        onClose={() => setPhase('idle')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={phase === 'error' ? 'error' : 'success'}
          onClose={() => setPhase('idle')}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

// ---------------------------------------------------------------------------
// Category + items browser
// ---------------------------------------------------------------------------

// Build a lookup so we can walk parent_id chains for the breadcrumb.
const categoriesById = (cats: AdminCategory[]): Map<string, AdminCategory> => {
  const m = new Map<string, AdminCategory>();
  for (const c of cats) m.set(c.id, c);
  return m;
};

const childrenOf = (
  cats: AdminCategory[],
  parentId: string,
): AdminCategory[] => {
  return cats
    .filter((c) => (c.parent_id ?? ROOT_PARENT_ID) === parentId)
    .sort((a, b) => {
      const ao = a.order ?? 0;
      const bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      return a.name.localeCompare(b.name);
    });
};

const pathTo = (
  cats: Map<string, AdminCategory>,
  categoryId: string,
): AdminCategory[] => {
  const path: AdminCategory[] = [];
  let cur: string | undefined = categoryId;
  while (cur) {
    const node = cats.get(cur);
    if (!node) break;
    path.unshift(node);
    cur = node.parent_id ?? undefined;
    if (cur === ROOT_PARENT_ID) break;
  }
  return path;
};

type CategoryParams = { tenant: string; categoryId?: string };

const CategoryPanel: React.FC = () => {
  const { idToken } = useAuth();
  const me = useWhoAmI();
  // Bulk flag editing — both the button and the underlying endpoint
  // are restricted to the `god` role. Plain admins can still toggle
  // individual flags via the per-card pills in edit mode; only god
  // gets the "flip every item in a category at once" affordance.
  const isGod = me.roles.includes('god');
  const { tenant, categoryId } = useParams<CategoryParams>();

  const [tenants, setTenants] = useState<ItemTenant[] | null>(null);
  const [cats, setCats] = useState<AdminCategory[] | null>(null);
  const [items, setItems] = useState<AdminItem[] | null>(null);
  const [error, setError] = useState<string>('');
  const [loadingItems, setLoadingItems] = useState(false);
  // Open edit dialog for this id. Null = no dialog open.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Open the dialog in create mode (no item id yet). The category the
  // admin is currently browsing is passed as the new item's default.
  const [creatingNew, setCreatingNew] = useState(false);
  // Bump after a successful save to force the items effect to refetch
  // so the grid reflects the new values.
  const [reloadTick, setReloadTick] = useState(0);
  // "+ New Category" dialog open state. Only surfaces when the active
  // tenant has `can_create_categories === true` on its `ItemTenant`
  // payload (frontroom today).
  const [creatingCategory, setCreatingCategory] = useState(false);
  // Quick-edit mode: when on, each item card grows a row of flag pills
  // (Special / Hidden / Discontinued / Deleted) the admin can toggle
  // without opening the per-item dialog. Persisted in localStorage so
  // an admin who flips it on doesn't have to re-enable it after every
  // navigation or refresh.
  const [quickEditMode] = useLocalStorageBool(
    LS_KEY_EDIT_FLAGS,
    false,
  );
  // "Show deleted": when off (default), the grid hides soft-deleted
  // items entirely — matches pre-soft-delete behavior. When on, deleted
  // rows render with a tombstone treatment (light-red bg + faded) so
  // the admin can locate and restore them. Persisted same as
  // quickEditMode so the admin's view preference follows them around.
  const [showDeleted] = useLocalStorageBool(
    LS_KEY_SHOW_DELETED,
    false,
  );
  // Bulk-flag dialog open state. Shared across the leaf-category and
  // branch-category render paths because both want the same dialog.
  const [bulkOpen, setBulkOpen] = useState(false);
  // Per-flag-per-item in-flight tracking, keyed `${itemId}-${flag}`.
  // A non-empty entry means the chip is mid-PATCH; the UI shows a
  // spinner and disables the chip until the response lands.
  const [savingFlags, setSavingFlags] = useState<Set<string>>(new Set());
  // One-shot error feedback for failed flag toggles. The optimistic UI
  // reverts independently; this just lets the admin see WHY it reverted.
  const [flagError, setFlagError] = useState<string>('');
  // Square sync subsystem status. While `configured && !cache_warm`,
  // the backend refuses `admin_create_item` (the catalog is needed for
  // dedupe before pushing a fresh item to Square), so we grey out the
  // "+ Add item" buttons and tell the admin to run Sync first. Fetched
  // once on mount per CategoryPanel — re-mounts on tenant navigation,
  // so naturally refreshes after a sync once the admin navigates.
  const [squareSync, setSquareSync] = useState<SquareSyncStatus | null>(null);
  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    getSquareSyncStatus(idToken)
      .then((status) => {
        if (!cancelled) setSquareSync(status);
      })
      .catch(() => {
        // Non-fatal — leave squareSync as null (treated as "let the
        // backend decide"). The button stays enabled; a doomed create
        // attempt will surface the 503 in the dialog.
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  const tenantEntry = tenants?.find((t) => t.slug === tenant);
  const imageRoot = tenantEntry?.image_root ?? '';
  const canCreateCategories = tenantEntry?.can_create_categories ?? false;
  // A tenant that doesn't push items to Square bypasses the "Square
  // cache must be warm" gate — there's no downstream sync to dedupe
  // for. Read off the API rather than assumed here: all three live
  // shops sync today, and only the florist archive doesn't.
  // `tenantEntry === undefined` (tenants list still loading) defaults
  // to `true` so the optimistic disable still kicks in on first paint.
  const isSyncedToSquare = tenantEntry?.is_synced_to_square ?? true;
  const tenantLabel = tenantEntry?.label ?? tenant;

  // Only enforce the cache-warm gate for tenants that actually push
  // to Square; for one that doesn't, nothing calls the Square sync
  // path and the cache state is irrelevant.
  const addItemDisabled =
    isSyncedToSquare && Boolean(squareSync?.configured && !squareSync?.cache_warm);
  const addItemTooltip = addItemDisabled
    ? 'Run "Sync to Square" first — the Square catalog cache must be warm before a new item can be safely pushed.'
    : '';

  /**
   * Toggle a single boolean flag on one item. Optimistic: flip locally
   * first, then PATCH. On failure, revert and surface the error in the
   * page-level snackbar.
   *
   * Patching one flag at a time matches the existing `ItemPatch` shape
   * — the backend only writes keys that are present — so accidental
   * cross-flag stomping isn't possible even if a previous PATCH for a
   * different flag is still in flight.
   */
  const handleFlagToggle = async (
    itemId: string,
    flag: FlagKey,
  ) => {
    if (!idToken) return;
    const item = items?.find((i) => i.id === itemId);
    if (!item) return;
    // `deleted` is tracked by `deleted_at` on the server (timestamp) and
    // exposed on AdminItem as `deleted_at`; the other flags are booleans
    // under `flags.*`. Branch on the source of truth per flag so a
    // tombstone card reads its state from the right field.
    const previous =
      flag === 'deleted'
        ? !!item.deleted_at
        : !!item.flags?.[flag];
    const next = !previous;
    const saveKey = `${itemId}-${flag}`;
    // Optimistic flip — for `deleted`, mirror the server's stamp/clear
    // semantics so the tombstone styling kicks in (or lifts) immediately.
    setItems((prev) =>
      prev?.map((i) => {
        if (i.id !== itemId) return i;
        if (flag === 'deleted') {
          return { ...i, deleted_at: next ? new Date().toISOString() : null };
        }
        return { ...i, flags: { ...(i.flags ?? {}), [flag]: next } };
      }) ?? prev,
    );
    setSavingFlags((prev) => {
      const nextSet = new Set(prev);
      nextSet.add(saveKey);
      return nextSet;
    });
    try {
      await patchItem(idToken, tenant, itemId, { [flag]: next });
    } catch (e) {
      // Revert the optimistic flip.
      setItems((prev) =>
        prev?.map((i) => {
          if (i.id !== itemId) return i;
          if (flag === 'deleted') {
            return { ...i, deleted_at: previous ? new Date().toISOString() : null };
          }
          return { ...i, flags: { ...(i.flags ?? {}), [flag]: previous } };
        }) ?? prev,
      );
      setFlagError(
        e instanceof ApiError
          ? `Couldn't update ${flag}: API error ${e.status}`
          : `Couldn't update ${flag}: ${(e as Error)?.message || 'unknown error'}`,
      );
    } finally {
      setSavingFlags((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(saveKey);
        return nextSet;
      });
    }
  };

  // Tenants (for image_root + label). Cheap; same fetch as the picker.
  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    listItemTenants(idToken)
      .then((rows) => {
        if (!cancelled) setTenants(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load tenants');
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  // Categories: refetch when the tenant changes or after a new
  // category is created (reloadTick bump).
  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setCats(null);
    setError('');
    listCategories(idToken, tenant)
      .then((rows) => {
        if (!cancelled) setCats(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err instanceof ApiError && err.status === 404) {
            setError('Unknown tenant.');
          } else {
            setError(err?.message || 'Failed to load categories');
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, tenant, reloadTick]);

  // Items: only fetch when a category is selected.
  useEffect(() => {
    if (!idToken) return;
    if (!categoryId) {
      setItems(null);
      return;
    }
    let cancelled = false;
    setLoadingItems(true);
    setItems(null);
    listItemsByCategory(idToken, tenant, categoryId, { includeDeleted: showDeleted })
      .then((rows) => {
        if (cancelled) return;
        // Admin sort: in-stock first, then by name. Matches tqos-admin.
        // Deleted items always sink to the bottom regardless of stock —
        // an in-stock-but-deleted row reading as "first" would be
        // misleading.
        rows.sort((a, b) => {
          const aDel = !!a.deleted_at;
          const bDel = !!b.deleted_at;
          if (aDel !== bDel) return aDel ? 1 : -1;
          const aIn = a.stock > 0;
          const bIn = b.stock > 0;
          if (aIn !== bIn) return aIn ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
        setItems(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load items');
      })
      .finally(() => {
        if (!cancelled) setLoadingItems(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, tenant, categoryId, reloadTick, showDeleted]);

  const catLookup = useMemo(() => (cats ? categoriesById(cats) : new Map()), [cats]);

  const currentChildren: AdminCategory[] = useMemo(() => {
    if (!cats) return [];
    return childrenOf(cats, categoryId ?? ROOT_PARENT_ID);
  }, [cats, categoryId]);

  const breadcrumb = useMemo(() => {
    if (!cats || !categoryId) return [];
    return pathTo(catLookup, categoryId);
  }, [cats, catLookup, categoryId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!cats || !tenants) return <CenteredSpinner />;

  return (
    <Stack spacing={3}>
      {/* Search bar lives at the top so it's available regardless of
          how deep in the category tree the admin has navigated. */}
      <ItemSearchBar />
      <BreadcrumbBar
        tenantSlug={tenant}
        tenantLabel={tenantLabel}
        trail={breadcrumb}
      />

      {/* Always-visible view toggles. The bar reads the same
          localStorage keys here as in `TenantsPanel` so flipping the
          state on either view is reflected on the other. */}
      <Box sx={{ mt: -1 }}>
        <ViewTogglesBar />
      </Box>

      {/* Top-level "Add item" — visible at the tenant root so the admin
          can drop a new item in without first walking down to its leaf
          category. The dialog leaves Category unselected; the admin
          picks one from the dropdown before saving. */}
      {!categoryId && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: -1,
          }}
        >
          {canCreateCategories && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCreatingCategory(true)}
              sx={{ textTransform: 'none' }}
            >
              + New category
            </Button>
          )}
          <Tooltip title={addItemTooltip}>
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={() => setCreatingNew(true)}
                disabled={addItemDisabled}
                sx={{ textTransform: 'none' }}
              >
                + Add item
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {currentChildren.length > 0 && (
        <>
          {/* Branch category (subcategories present): "+ New category"
              for tenants that allow it (frontroom today), and "Bulk
              edit" for god-role. Both can coexist; the bar collapses
              when neither applies for non-god admins on Mongo tenants. */}
          {categoryId && (canCreateCategories || isGod) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: -1,
              }}
            >
              {canCreateCategories && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCreatingCategory(true)}
                  sx={{ textTransform: 'none' }}
                >
                  + New category
                </Button>
              )}
              {isGod && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setBulkOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Bulk edit
                </Button>
              )}
            </Box>
          )}
          <CategoryGrid
            tenant={tenant}
            categories={currentChildren}
            imageRoot={imageRoot}
          />
        </>
      )}

      {categoryId && (
        <>
          {/* A category holds either child categories OR items — never
              both. Showing "+ Add item" / quick-edit in a
              category-of-categories would either produce an item with
              the wrong category_id or have nothing to act on. Hide the
              row entirely in that case. The "+ New category" button is
              the exception: adding a subcategory to a leaf is a valid
              first step toward turning it into a branch, so surface
              that even when items are present. */}
          {currentChildren.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1.5,
                mt: -1,
              }}
            >
              {canCreateCategories && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCreatingCategory(true)}
                  sx={{ textTransform: 'none' }}
                >
                  + New category
                </Button>
              )}
              {isGod && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setBulkOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Bulk edit
                </Button>
              )}
              <Tooltip title={addItemTooltip}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setCreatingNew(true)}
                    disabled={addItemDisabled}
                    sx={{ textTransform: 'none' }}
                  >
                    + Add item
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
          <ItemsGrid
            items={items}
            loading={loadingItems}
            imageRoot={imageRoot}
            quickEditMode={quickEditMode}
            savingFlags={savingFlags}
            onItemClick={(id) => setEditingId(id)}
            onFlagToggle={handleFlagToggle}
          />
        </>
      )}

      <ItemEditDialog
        open={editingId !== null || creatingNew}
        tenant={tenant}
        imageRoot={imageRoot}
        skuPrefix={tenantEntry?.sku_prefix ?? ''}
        itemId={editingId}
        defaultCategoryId={creatingNew ? categoryId : undefined}
        categories={cats ?? []}
        onClose={() => {
          setEditingId(null);
          setCreatingNew(false);
        }}
        onSaved={() => setReloadTick((n) => n + 1)}
      />

      <CreateCategoryDialog
        open={creatingCategory}
        tenant={tenant}
        parentId={categoryId ?? '1'}
        parentLabel={
          categoryId ? catLookup.get(categoryId)?.name ?? '(unknown)' : 'Top level'
        }
        onClose={() => setCreatingCategory(false)}
        onCreated={() => {
          setCreatingCategory(false);
          setReloadTick((n) => n + 1);
        }}
      />

      {categoryId && isGod && (
        <BulkFlagDialog
          open={bulkOpen}
          tenant={tenant}
          categoryId={categoryId}
          categoryName={catLookup.get(categoryId)?.name ?? ''}
          // Branch categories want recursion by default (items live in
          // descendants); leaf categories don't need it (direct items
          // are the natural target). Operator can still toggle either
          // way in the dialog.
          defaultRecursive={currentChildren.length > 0}
          onClose={() => setBulkOpen(false)}
          onApplied={() => setReloadTick((n) => n + 1)}
        />
      )}

      <Snackbar
        open={flagError !== ''}
        autoHideDuration={5000}
        onClose={() => setFlagError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setFlagError('')}>
          {flagError}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

const BreadcrumbBar: React.FC<{
  tenantSlug: string;
  tenantLabel: string;
  trail: AdminCategory[];
}> = ({ tenantSlug, tenantLabel, trail }) => (
  <Breadcrumbs aria-label="category breadcrumb">
    <MuiLink
      component={Link}
      to="/items"
      underline="hover"
      sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}
    >
      <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
      Items
    </MuiLink>
    <MuiLink
      component={Link}
      to={`/items/${tenantSlug}`}
      underline="hover"
      sx={{ color: '#64748b' }}
    >
      {tenantLabel}
    </MuiLink>
    {trail.map((c, i) => {
      const isLast = i === trail.length - 1;
      if (isLast) {
        return (
          <Typography key={c.id} sx={{ color: '#1f2937', fontWeight: 500 }}>
            {c.name}
          </Typography>
        );
      }
      return (
        <MuiLink
          key={c.id}
          component={Link}
          to={`/items/${tenantSlug}/category/${encodeURIComponent(c.id)}`}
          underline="hover"
          sx={{ color: '#64748b' }}
        >
          {c.name}
        </MuiLink>
      );
    })}
  </Breadcrumbs>
);

/**
 * Inline "+ New category" dialog. Small enough that pulling it into
 * `components/dialogs/` adds more import noise than it removes. Name +
 * hidden toggle is the entire form; parent_id is carried in from the
 * caller (either the root marker or the currently-browsed category id).
 */
const CreateCategoryDialog: React.FC<{
  open: boolean;
  tenant: string;
  parentId: string;
  parentLabel: string;
  onClose: () => void;
  onCreated: (created: AdminCategory) => void;
}> = ({ open, tenant, parentId, parentLabel, onClose, onCreated }) => {
  const { idToken } = useAuth();
  const [name, setName] = useState('');
  const [hidden, setHidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  // Reset form whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setName('');
      setHidden(false);
      setError('');
      setSaving(false);
    }
  }, [open]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !saving;

  const handleSubmit = async () => {
    if (!idToken || !canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const created = await createCategory(idToken, tenant, {
        name: trimmed,
        parentId,
        hidden,
      });
      onCreated(created);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Server error (${err.status}): ${err.message || 'request failed'}`
          : (err as Error)?.message || 'Failed to create category';
      setError(msg);
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create category</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Parent: <strong>{parentLabel}</strong>
          </Typography>
          <TextField
            label="Name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 120 }}
            disabled={saving}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={hidden}
                onChange={(e) => setHidden(e.target.checked)}
                disabled={saving}
              />
            }
            label="Hidden"
          />
          {error && (
            <Typography variant="body2" sx={{ color: '#b91c1c' }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CategoryGrid: React.FC<{
  tenant: string;
  categories: AdminCategory[];
  imageRoot: string;
}> = ({ tenant, categories, imageRoot }) => (
  <Grid container spacing={2}>
    {categories.map((c) => (
      <Grid key={c.id} item xs={6} sm={4} md={3} lg={2}>
        <Card
          variant="outlined"
          sx={{
            opacity: c.hidden ? 0.55 : 1,
          }}
        >
          <CardActionArea
            component={Link}
            to={`/items/${tenant}/category/${encodeURIComponent(c.id)}`}
            sx={{ p: 1.5, textAlign: 'center' }}
          >
            <ItemImage id={c.image_id} imageRoot={imageRoot} title={c.name} size={140} />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                fontWeight: 500,
                color: '#1f2937',
                wordBreak: 'break-word',
              }}
            >
              {c.name}
            </Typography>
            {c.hidden && (
              <Chip label="hidden" size="small" sx={{ mt: 0.5 }} />
            )}
          </CardActionArea>
        </Card>
      </Grid>
    ))}
  </Grid>
);

type FlagKey =
  | 'special'
  | 'hidden'
  | 'discontinued'
  | 'deleted'
  | 'taxExempt';

// Tombstone palette: muted red wash over the normal `#fff` card +
// faded content. Brought up to the page level so the dialog and the
// grid use the same colour story for "soft-deleted" affordances.
const DELETED_BG = '#fdecec';
const DELETED_BORDER = '#f2c7c7';
const DELETED_OPACITY = 0.55;

const ItemsGrid: React.FC<{
  items: AdminItem[] | null;
  loading: boolean;
  imageRoot: string;
  /** When true, each card renders a row of Special/Hidden/Discontinued
   *  pills under the action area. */
  quickEditMode: boolean;
  /** Set of in-flight `${itemId}-${flag}` saves; the matching pill
   *  shows a spinner and is disabled until removed. */
  savingFlags: Set<string>;
  onItemClick: (id: string) => void;
  onFlagToggle: (itemId: string, flag: FlagKey) => void;
}> = ({
  items,
  loading,
  imageRoot,
  quickEditMode,
  savingFlags,
  onItemClick,
  onFlagToggle,
}) => {
  if (loading) return <CenteredSpinner />;
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
        No items in this category.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid key={item.id} item xs={6} sm={4} md={3} lg={2}>
          <ItemCard
            item={item}
            imageRoot={imageRoot}
            quickEditMode={quickEditMode}
            savingFlags={savingFlags}
            onClick={() => onItemClick(item.id)}
            onFlagToggle={onFlagToggle}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const ItemCard: React.FC<{
  item: AdminItem;
  imageRoot: string;
  quickEditMode: boolean;
  savingFlags: Set<string>;
  onClick: () => void;
  onFlagToggle: (itemId: string, flag: FlagKey) => void;
}> = ({
  item,
  imageRoot,
  quickEditMode,
  savingFlags,
  onClick,
  onFlagToggle,
}) => {
  const parent = isParentItem(item);
  const isDeleted = !!item.deleted_at;

  // Three independent visual states stack:
  //   - parent (amber background)
  //   - hidden (dimmed)
  //   - deleted (red wash + heavy fade — wins the colour fight)
  // Deleted takes precedence on opacity since it's the most decisive
  // signal: a deleted row shouldn't read as "barely there hidden", it
  // should read as "this is gone, restore it if you didn't mean to".
  const cardSx = {
    opacity: isDeleted
      ? DELETED_OPACITY
      : item.flags?.hidden
        ? 0.55
        : 1,
    position: 'relative' as const,
    backgroundColor: isDeleted ? DELETED_BG : parent ? '#fef3c7' : undefined,
    borderColor: isDeleted ? DELETED_BORDER : parent ? '#fcd34d' : undefined,
  };

  // Pills are the visual indicator for special/hidden/discontinued/deleted —
  // the dedicated row at the top of the card replaces the old corner-
  // chip overlays. The row shows enabled flags in both modes; edit
  // mode also adds the disabled (outlined) pills so the admin can
  // toggle them on. Parent items show the row read-only — never
  // clickable, since variant parents can't be edited from this view.
  const anyFlagEnabled =
    !!item.flags?.special ||
    !!item.flags?.hidden ||
    !!item.flags?.discontinued ||
    !!item.flags?.taxExempt ||
    isDeleted;
  const interactivePills = quickEditMode && !parent;
  const showPillRow = interactivePills || anyFlagEnabled;

  const inner = (
    <Box sx={{ p: 1.5 }}>
      <ItemImage
        id={item.image_id}
        imageRoot={imageRoot}
        title={item.name}
        size={140}
      />
      {parent && (
        <Chip
          label="parent"
          size="small"
          color="warning"
          variant="outlined"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: '#fff7ed',
          }}
        />
      )}
      {item.brand && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: '#64748b', mt: 1 }}
        >
          {item.brand}
        </Typography>
      )}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: '#1f2937',
          wordBreak: 'break-word',
          minHeight: '2.5em',
        }}
      >
        {item.name}
      </Typography>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mt: 1 }}
      >
        <Box>
          <Price price={item.price} showZero />
        </Box>
        <Typography
          variant="caption"
          sx={{ color: stockColor(item.stock), fontWeight: 500 }}
        >
          {item.stock} in stock
        </Typography>
      </Stack>
    </Box>
  );

  // Pill row sits ABOVE the CardActionArea so clicking a pill doesn't
  // bubble up and open the edit dialog. Rendered identically across the
  // parent and non-parent branches — only `interactivePills` differs.
  const pillRow = showPillRow ? (
    <FlagPillRow
      item={item}
      savingFlags={savingFlags}
      readOnly={!interactivePills}
      onToggle={(flag) => onFlagToggle(item.id, flag)}
    />
  ) : null;

  if (parent) {
    return (
      <Tooltip title="Parent items can't be edited from here yet — variant editing is coming soon.">
        <Card variant="outlined" sx={{ ...cardSx, cursor: 'not-allowed' }}>
          {pillRow}
          {inner}
        </Card>
      </Tooltip>
    );
  }

  return (
    <Card variant="outlined" sx={cardSx}>
      {pillRow}
      <CardActionArea onClick={onClick}>{inner}</CardActionArea>
    </Card>
  );
};

const FLAG_LABELS: Record<FlagKey, string> = {
  special: 'Special',
  hidden: 'Hidden',
  discontinued: 'Discontinued',
  deleted: 'Deleted',
  taxExempt: 'Zero Rated',
};

const FLAG_COLORS: Record<FlagKey, 'warning' | 'default' | 'error' | 'info'> =
  {
    special: 'warning',
    hidden: 'default',
    discontinued: 'error',
    deleted: 'error',
    taxExempt: 'info',
  };

const FlagPillRow: React.FC<{
  item: AdminItem;
  savingFlags: Set<string>;
  /** When true, the row is display-only: pills are not clickable and
   *  only currently-enabled flags appear. When false, every pill
   *  appears (off ones as outlined) and clicking toggles them. */
  readOnly: boolean;
  onToggle: (flag: FlagKey) => void;
}> = ({ item, savingFlags, readOnly, onToggle }) => {
  const allFlags: FlagKey[] = [
    'special',
    'hidden',
    'discontinued',
    'deleted',
    'taxExempt',
  ];
  // `deleted` lives on `deleted_at` (timestamp), not `flags`, so the
  // read-side check has to branch. Keep the pill itself uniform — the
  // user shouldn't see a special-case affordance.
  const isFlagOn = (flag: FlagKey): boolean =>
    flag === 'deleted' ? !!item.deleted_at : !!item.flags?.[flag];
  const visibleFlags = readOnly
    ? allFlags.filter(isFlagOn)
    : allFlags;
  if (visibleFlags.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        flexWrap: 'wrap',
        px: 1.5,
        pt: 1.5,
      }}
    >
      {visibleFlags.map((flag) => {
        const isOn = isFlagOn(flag);
        const saving = savingFlags.has(`${item.id}-${flag}`);
        const clickable = !readOnly && !saving;
        return (
          <Chip
            key={flag}
            label={FLAG_LABELS[flag]}
            size="small"
            color={isOn ? FLAG_COLORS[flag] : 'default'}
            variant={isOn ? 'filled' : 'outlined'}
            onClick={clickable ? () => onToggle(flag) : undefined}
            icon={saving ? <CircularProgress size={12} /> : undefined}
            sx={{
              fontSize: '0.7rem',
              height: 22,
              cursor: readOnly ? 'default' : saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          />
        );
      })}
    </Box>
  );
};

const CenteredSpinner: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <CircularProgress size={24} />
  </Box>
);

// ---------------------------------------------------------------------------
// Page-level switch
// ---------------------------------------------------------------------------

const ItemsPage: React.FC = () => {
  const { tenant } = useParams<{ tenant?: string }>();
  // No tenant in URL → picker; otherwise the browser handles both
  // "tenant only" and "tenant + categoryId" states (categoryId is
  // optional within CategoryPanel and read from useParams there).
  if (!tenant) return <TenantsPanel />;
  return <CategoryPanel />;
};

export default ItemsPage;
